/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { SafetyCertificateOutlined, UserOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import type { StartRole } from '@sportninja/common/features/registration/startStep/types'
import RoleSelectionCard from '../../../RoleSelectionCard'

interface Props {
  /** The chosen role, or null while the question is still open. */
  value: StartRole | null
  onChange: (role: StartRole) => void
  /** Whether the competition offers each side at all. */
  playerAvailable: boolean
  teamAvailable: boolean
  /** Every division that takes teams is out of spots. */
  teamFull?: boolean
}

/**
 * The "what are you registering?" question.
 *
 * Renders only the sides the divisions actually offer, so a competition taking
 * players alone never shows a Team card. A `?type=` link pre-selects a side but
 * leaves both choosable — only a genuine capacity limit disables a card.
 */
const RoleZone = ({
  value,
  onChange,
  playerAvailable,
  teamAvailable,
  teamFull = false,
}: Props) => {
  const onlyOneSide = !playerAvailable || !teamAvailable

  return (
    <div css={[rowStyle, onlyOneSide && narrowRowStyle]}>
      {playerAvailable ? (
        <RoleSelectionCard
          title='Player Registration'
          subtitle='Join a Team'
          icon={<UserOutlined css={iconStyle} />}
          compact
          onClick={() => onChange('player')}
          selected={value === 'player'}
        />
      ) : null}

      {teamAvailable ? (
        <RoleSelectionCard
          title='Team Registration'
          subtitle='Register as a Team Rep'
          icon={<SafetyCertificateOutlined css={iconStyle} />}
          compact
          onClick={() => onChange('team')}
          selected={value === 'team'}
          // A full division explains itself through the card's own default
          // reason. A `?type=` link does NOT disable the other side — it
          // pre-selects only, so either card stays choosable.
          disabled={teamFull}
        />
      ) : null}
    </div>
  )
}

export default RoleZone

const rowStyle = css`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const narrowRowStyle = css`
  width: 50%;
  align-self: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`

/* Sized against the card's 70px icon well. antd icons render as glyphs, so
   font-size is their size control here, not type. */
const iconStyle = css`
  font-size: 36px;
  color: ${colors.PRIMARY};
`
